import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useTablePrefilterContext } from '@work4all/components/lib/components/table/TablePrefilterProvider';

import { nameof } from '@work4all/data/lib/helper/nameof';

import { Appointment } from '@work4all/models/lib/Classes/Appointment.entity';
import { SortDirection } from '@work4all/models/lib/DataProvider';
import { EMailTemplateKind } from '@work4all/models/lib/Enums/EMailTemplateKind.enum';
import { EMode } from '@work4all/models/lib/Enums/EMode.enum';

import { EntityTableV8, IEntityTableV8 } from '../entity-table/EntityTableV8';
import { useEntityDataTableV8 } from '../entity-table/use-entity-data-table-v8';
import { useDeleteAppointments } from '../project-table/components/project-table-bottom-section/components/resources-timeline/hooks/use-delete-appointments';
import schema from '../schemata/appointment-table-schema.json';

import { useAppointmentEmailTemplate } from './hooks/use-appointment-email-template';
import { useGetAppoinmentEmailsParams } from './hooks/use-get-appoinment-emails-params';

const APPOINTMENT_FORCE_REQUEST_FIELDS: Appointment<EMode.query> = {
  fromAbsolute: null,
  toAbsolute: null,
  appointmentAttendeeList: [
    {
      id: null,
      ressource: {
        id: null,
        eMail: null,
        displayName: null,
        loginName: null,
      },
      user: {
        id: null,
        eMail: null,
        displayName: null,
        loginName: null,
      },
      contact: {
        id: null,
        eMail: null,
        displayName: null,
      },
      businessPartner: {
        id: null,
        data: {
          customer: { id: null, eMail: null },
          supplier: { id: null, eMail: null },
        },
      },
    },
  ],
  contact: {
    id: null,
    displayName: null,
    eMail: null,
  },
  startDate: null,
  endDate: null,
  isWholeDay: null,
  city: null,
  title: null,
  note: null,
  creatorId: null,
  contract: {
    id: null,
    contractNumber: null,
  },
  deliveryNote: {
    id: null,
    number: null,
  },
};

const defaultSort = [
  {
    field: nameof<Appointment>('startDate'),
    direction: SortDirection.DESCENDING,
  },
];

export function AppointmentsTable(props: IEntityTableV8) {
  const { prefilter } = useTablePrefilterContext();
  const { t } = useTranslation();

  const dataTable = useEntityDataTableV8<Appointment, never>({
    ...props,
    schema: schema as never,
    prefilter,
    defaultSort,
    forceRequestFields: APPOINTMENT_FORCE_REQUEST_FIELDS,
  });

  // TODO Find a way to apply the fix to the data if it's still needed
  // Fix appointment start and end dates for full day appointments.
  // const data = useFixedAppointmentsData(dataTable.data);

  const getEmailParams = useGetAppoinmentEmailsParams(dataTable.selectedEntity);

  const action = useAppointmentEmailTemplate({
    getEmailParams,
    eMailTemplateKind: EMailTemplateKind.APPOINTMENT_NOTIFICATION,
    noTemplate: true,
    appointments: dataTable.selectedEntities,
  });

  const { deleteAppointmentForAllAttendees } = useDeleteAppointments();

  const hasMultipleAttendees =
    dataTable.selectedEntity?.appointmentAttendeeList?.length > 1;

  const actions = useMemo(() => {
    const commonActions = {
      custom: {
        right: [action],
      },
    };
    if (hasMultipleAttendees) {
      return {
        ...commonActions,
        remove: {
          handler: (selected) =>
            deleteAppointmentForAllAttendees(Number(selected[0])),
          maxCount: 1,
          description: t('ALERTS.APPOINTMENTS.DELETE_ALL'),
        },
      };
    }
    return commonActions;
  }, [action, deleteAppointmentForAllAttendees, hasMultipleAttendees, t]);

  return <EntityTableV8 {...props} {...dataTable} actions={actions} />;
}
